import { CloseButton, TextInput, type TextInputProps } from '@mantine/core';
import { type ChangeEvent, type KeyboardEvent, type ReactNode, useCallback, useEffect } from 'react';
import { useDigitRegister, type UseDigitRegisterOptions } from './useDigitRegister';

export interface DigitRegisterInputProps extends Omit<TextInputProps, 'value' | 'defaultValue' | 'onChange' | 'type'> {
    digits: string;
    onDigitsChange: (digits: string) => void;
    maxDigits: number;
    format: (digits: string) => string;
    isDigitAllowed?: UseDigitRegisterOptions['isDigitAllowed'];
    clearable?: boolean;
}

export const DigitRegisterInput = ({
    digits,
    onDigitsChange,
    maxDigits,
    format,
    isDigitAllowed,
    clearable = true,
    size = 'xs',
    rightSection,
    disabled,
    readOnly,
    onKeyDown: consumerOnKeyDown,
    autoFocus,
    ...textInputProps
}: DigitRegisterInputProps) => {
    const { inputProps, clear, blur, focus } = useDigitRegister({
        digits,
        onDigitsChange,
        maxDigits,
        isDigitAllowed,
    });

    useEffect(() => {
        if (!autoFocus) {
            return;
        }
        const id = requestAnimationFrame(focus);
        return () => cancelAnimationFrame(id);
        // changing `autoFocus` later shouldn't re-focus
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = format(digits);

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            if (event.target.value !== value) {
                event.target.value = value;
            }
        },
        [value]
    );

    // Compose prop-supplied onKeyDown with the hooks
    const hookOnKeyDown = inputProps.onKeyDown;
    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            consumerOnKeyDown?.(event);
            if (event.defaultPrevented) {
                return;
            }
            hookOnKeyDown(event);
        },
        [consumerOnKeyDown, hookOnKeyDown]
    );

    const handleClearMouseDown = (event: React.MouseEvent) => {
        event.preventDefault();
    };

    const handleClearClick = () => {
        clear();
        // defer the blur until after onChange
        queueMicrotask(blur);
    };

    const showClearButton = clearable && !disabled && !readOnly && digits.length > 0;

    const resolvedRightSection: ReactNode = showClearButton ? (
        <CloseButton aria-label="Clear" onMouseDown={handleClearMouseDown} onClick={handleClearClick} size="xs" />
    ) : (
        rightSection
    );

    return (
        <TextInput
            {...textInputProps}
            {...inputProps}
            onKeyDown={handleKeyDown}
            value={value}
            onChange={handleChange}
            size={size}
            disabled={disabled}
            readOnly={readOnly}
            rightSection={resolvedRightSection}
            autoFocus={autoFocus}
        />
    );
};
